import * as Sentry from '@sentry/react'
import cn from 'classnames'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-modal'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ReactResizeDetector from 'react-resize-detector'
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'
import { appInit } from '../../../config'
import { setSentryUser } from '../../../sentryUser'
import { changeIBScreen } from 'ib/redux/state'
import { useIBState, useShouldShowIBportal } from 'ib/utils/hooks'
import { useRedirect as ibRedirect } from 'ib/utils/useRedirect'
import { getIBScreenType } from 'ib/utils/utils'
import { logout } from '../../../redux/actions'
import { AppState } from '../../../redux/appState'
import { changeScreen } from "../../../redux/slices/app"
import { hideSidebar } from '../../../redux/slices/dashboard'
import { Store } from '../../../redux/store'
import api from '../../../utils/api'
import { __STORAGE, BREAKPOINTS, ScreenSize, WtrPageView } from "../../../utils/enums"
import { useAccounts, useAccountVerifiedEvent, useKycApprovedEvent, useLandingPage, useRedirectToWTRhandler, useUserApplications } from '../../../utils/hooks'
import { accountPath, default as appRoutes, dashboardPath, externalRoutes, ibPath, profilePath, default as Routes } from '../../../utils/routes'
import { useWtrPostMessageListener } from '../../../utils/useWtrPostMessageListener'
import Utils from '../../../utils/utils'
import DemoExpirationWarning from '../../DemoExpirationWarning'
import { hideGlobalLoader } from '../../GlobalLoader'
import SubscriptionTerminatedError from '../../SubscriptionTerminatedError'
import TermsAndConditions from '../../TermsAndConditions'
import Account from '../Account'
import AppErrorBoundary from './AppErrorBoundary'
import styles from './style.module.scss'

const Error = React.lazy(() => import('../../Error'))
const Migration = React.lazy(() => import('../../Presentational/Migration'))
const Dashboard = React.lazy(() => import('../Dashboard'))
const Onboarding = React.lazy(() => import('../Onboarding'))
const IB = React.lazy(() => import('../../../ib'))
const Profile = React.lazy(() => import('../Profile'))

Modal.setAppElement('body')

interface AppProps extends RouteComponentProps {
}

const App: FC<AppProps> = ({ history, location }) => {
    const dispatch = useDispatch()
    const [ready, setready] = useState(false)
    const shouldShowIBportal = useShouldShowIBportal()
    const landingPage = useLandingPage()

    const store: AppState = useSelector(
        (state: Store) => state.App)
    const bootCompletionCount = useSelector((state: Store) => state.App.bootCompletionCount)

    const wtrPostMessageListener = useWtrPostMessageListener()
    const { latestApplication, hasApproved } = useUserApplications()
    const handleRedirectToWTR = useRedirectToWTRhandler('_self')
    const { liveAccounts } = useAccounts()
    const liveTT = liveAccounts.find(x => x.account.platform.name === 'ThinkTrader')
    const { application: ibApplication } = useIBState()
    const { redirectByStatus: ibRedirectByStatus } = ibRedirect()
    const hasComissionAccount = useMemo(() => liveAccounts.find(x => x.platformAccountType === 'Commission'), [liveAccounts])

    const screenSize = useSelector(
        (state: Store) => state.App.screen,
        shallowEqual
    )


    const onResize = (width: number, height: number) => {
        const root = document.getElementById("root")
        if (!width) return
        switch (true) {
            case width < BREAKPOINTS.MOBILE:
                dispatch(changeScreen(ScreenSize.Mobile))
                dispatch(hideSidebar())
                root?.setAttribute("screen", "mobile")
                break
            case width > BREAKPOINTS.DESKTOP:
                dispatch(changeScreen(ScreenSize.Desktop))
                root?.removeAttribute("screen")
                break
            default:
                dispatch(changeScreen(ScreenSize.Tablet))
                root?.removeAttribute("screen")
                break
        }
        dispatch(changeIBScreen(getIBScreenType(width)))
    }

    const {
        loggedIn,
        notificationMap,
        userProfile,
        redirectedFromWTR
    } = store

    /*     useEffect(() => {
            initCaptcha(language, userCountry?.code3)
        }, [initCaptcha, language, userCountry])
     */
    useEffect(() => {
        window.navigateToPage = (url: string) => history.push(url)
    }, [history])

    useEffect(() => {
        const syncScreenSize = () => onResize(window.innerWidth, window.innerHeight)
        syncScreenSize()
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') syncScreenSize()
        }
        window.addEventListener('focus', syncScreenSize)
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => {
            window.removeEventListener('focus', syncScreenSize)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const ssoIntoWTR = useCallback(async () => {
        const status = new URLSearchParams(window.location.search).get(WtrPageView.Status) as string
        const transactionId = new URLSearchParams(window.location.search).get('transaction_id') as string

        try {
            const response = await api.getPaymentInfo({
                transaction_id: transactionId,
            })

            handleRedirectToWTR(liveTT?.account, 'LIVE', {
                targetPage: WtrPageView.Status,
                status,
                transactionId,
                accountNumber: response.payload[0].result.account.accountNumber
            })
        }
        catch {
            console.log('failed to retrieve transaction data')
        }

        return
    }, [handleRedirectToWTR, liveTT?.account])

    useEffect(() => {
        const checkWTR = async () => {
            await appInit
            const sso = new URLSearchParams(window.location.search).get(WtrPageView.SSO) as string
            if (sso && userProfile) {
                await ssoIntoWTR()
                return
            } else if (redirectedFromWTR && loggedIn) {
                Utils.postMessageToWTR({ message: 'loggedIn' })
            }
        }
        // TMJV-936: await appInit (and any other bootstrap step) can reject with a
        // bare {code, message} payload; swallow it here so a non-fatal WTR bootstrap
        // check can never escape as a global unhandled promise rejection.
        checkWTR().catch(() => undefined)
    }, [ssoIntoWTR, loggedIn, redirectedFromWTR, userProfile])

    useEffect(() => {
        // Listen to post messages from ThinkTrader
        window.addEventListener('message', wtrPostMessageListener)

        return () => {
            window.removeEventListener('message', wtrPostMessageListener)
        }
    }, [wtrPostMessageListener])

    useAccountVerifiedEvent()
    useKycApprovedEvent()

    // Set Sentry user context (masked) when profile is available, clear it otherwise
    useEffect(() => {
        setSentryUser(userProfile?.id)
    }, [userProfile])

    const { welcome, account, register } = appRoutes

    const redirectIfNotLogged = useCallback((path) => {
        const pathnameWithoutTrailingSlash = Utils.removeTrailingSlash(path)
        return ![
            account.createAccount.live,
            account.createAccount.demo,
            account.personalInformation,
            account.login,
            account.check2fa,
            account.callback,
            account.callbackDemo,
            account.socialRegistration,
            welcome.demo,
            register.root,
            register.live,
            register.demo,
            register.ib,
            account.resetPasswordStep1,
            externalRoutes.scanId.success,
            externalRoutes.scanId.failure
        ].includes(pathnameWithoutTrailingSlash)
    }, [account, register, welcome])

    const lastLandedBootCountRef = useRef(0)
    useEffect(() => {
        // THPRT-6209: the boot saga increments bootCompletionCount at every landing
        // point (success, auth-fail, init error). Run the routing decision once per
        // increment; the ref guard stops it re-firing when unrelated deps change.
        if (bootCompletionCount === lastLandedBootCountRef.current) return
        lastLandedBootCountRef.current = bootCompletionCount

        const { passwordResetToken, loggedIn, appStatus,
        } = store
        if (passwordResetToken) {
            history.push(appRoutes.account.resetPasswordStep3)
        } else if (!loggedIn) {
            if (redirectIfNotLogged(window.location.pathname)) {
                history.push(appRoutes.account.login)
            }
        } else if (
            window.location.pathname === appRoutes.dashboard.funds.status &&
            !!new URLSearchParams(window.location.search).get('transaction_id')
        ) {
            // THPRT-6317: a Checkout Flow deposit returns to the result page via a
            // full-page reload (the 3DS redirect), which re-runs the landing decision on
            // cold boot. Pushing landingPage here would drop the transaction_id from the
            // URL and make DepositStatus show "Failed to load payment information"
            // for a deposit that actually succeeded. Leave the deep link intact so
            // the result page can resolve the payment.
        } else if (window.location.pathname === appRoutes.account.socialRegistration && !appStatus) {
            // Social registration with no app yet, don't redirect, let the page handle its own flow
        } else if (ibApplication || hasComissionAccount) {
            ibRedirectByStatus()
        } else {
            switch (appStatus) {
                case 'PENDING_ID_ADDRESS':
                case 'PENDING_ID':
                case 'PENDING_ADDRESS':
                case 'PENDING_KYC':
                    if (hasApproved) {
                        history.push(landingPage)
                    } else {
                        history.push(appRoutes.account.verify.root)
                    }
                    break
                case 'APPROVED':
                case 'PENDING_APPROPRIATENESS_TEST':
                    history.push(landingPage)
                    break
                case 'INCOMPLETE':
                    if (hasApproved) {
                        history.push(landingPage)
                    } else {
                        history.push(appRoutes.onboarding)
                    }
                    break
                default:
                    history.push(landingPage)
            }
        }
        hideGlobalLoader()
        setready(true)
    }, [bootCompletionCount, hasComissionAccount, history, ibApplication, ibRedirectByStatus, landingPage, latestApplication, redirectIfNotLogged, store, hasApproved])

    useEffect(() => {
        const unlisten = window.addEventListener('TokenExpired', () => {
            if (loggedIn) {
                dispatch(logout())
                history.push(appRoutes.account.login)
            }
        })
        return unlisten
    }, [dispatch, history, loggedIn])

    useEffect(() => {
        const unlisten = history.listen((loc, action) => {
            const safePath = loc.pathname.replace(
                /\/(reset-password|kyc|oauth|invite|verify)\/[^/]+/g,
                '/$1/[redacted]'
            )
            Sentry.addBreadcrumb({
                category: 'navigation',
                message: `${action} ${safePath}`,
                level: 'info',
            })
            if (loggedIn && notificationMap.length && !loc.pathname.includes('login')) {
                // dispatch(getNotifications())
                if (screenSize === ScreenSize.Mobile)
                    dispatch(hideSidebar())
            }
        })
        return unlisten
    }, [dispatch, history, loggedIn, notificationMap.length, screenSize])

    const click = useCallback(() => {
        Utils.writeTimestamp(__STORAGE.lastClick)
    }, [])

    if (!ready) return null

    const className = cn(
        styles.app,
        {
            'rtl': store.rtl,
            [styles.mobile]: screenSize === ScreenSize.Mobile
        }
    )

    return (<AppErrorBoundary>
        <div className={className} onClick={click}>
            <ReactResizeDetector
                handleWidth
                // handleHeight
                refreshMode="debounce"
                refreshRate={100}
                onResize={onResize} />

            <Switch>
                <Route path={accountPath} component={Account} />
                {loggedIn && [
                    <Route path={dashboardPath} key={dashboardPath} component={Dashboard} />,
                    <Route path={profilePath} key={profilePath} component={Profile} />,
                    <Route path={Routes.onboarding} key={Routes.onboarding} component={Onboarding} />,
                    shouldShowIBportal && <Route path={ibPath} key={ibPath} component={IB} />
                ]}
                <Redirect key='app-redirect' to={appRoutes.dashboard.accounts.root} />
            </Switch>

            <Migration />
            <TermsAndConditions />
            <DemoExpirationWarning />
            <SubscriptionTerminatedError />
            <Error />
        </div>
    </AppErrorBoundary>)
}

export default App